import { Combobox } from '@base-ui/react/combobox';
import { ChevronDown, X } from 'lucide-react';
import { cn } from '../../lib/cn.ts';
import { chipRemoveVariants, chipVariants } from '../chip/index.ts';
import type { SearchSelectLabels } from './search-select-labels.ts';

const controlClass = cn(
  'flex shrink-0 items-center justify-center rounded-full p-1 text-ink-muted transition',
  'hover:bg-fill hover:text-ink',
  'focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-ink-faint',
);
const inputClass =
  'min-w-0 flex-1 bg-transparent text-base font-medium text-ink outline-none placeholder:text-ink-muted';

interface SearchSelectInputGroupProps<T> {
  getKey: (item: T) => string;
  getLabel: (item: T) => string;
  placeholder?: string;
  id?: string;
  ariaLabel?: string;
  ariaDescribedBy?: string;
  disabled: boolean;
  clearable: boolean;
  invalid: boolean;
  multiple: boolean;
  hasValue: boolean;
  labels: SearchSelectLabels;
  className?: string;
}

/** The field itself: the picks as chips or the text, the clear button and the list's toggle. */
export function SearchSelectInputGroup<T>({
  getKey,
  getLabel,
  placeholder,
  id,
  ariaLabel,
  ariaDescribedBy,
  disabled,
  clearable,
  invalid,
  multiple,
  hasValue,
  labels,
  className,
}: SearchSelectInputGroupProps<T>) {
  return (
    <Combobox.InputGroup
      data-slot='search-select'
      className={cn(
        'flex w-full items-center gap-1 rounded-md border border-transparent bg-fill-faint pr-1.5',
        'transition focus-within:shadow-focus',
        invalid && 'border-danger-line bg-danger-fill/50',
        disabled && 'opacity-40',
        multiple && 'flex-wrap p-1.5',
        className,
      )}
    >
      {multiple ? (
        <Combobox.Chips className='flex flex-1 flex-wrap items-center gap-1.5'>
          <Combobox.Value>
            {(picked: T[]) => (
              <>
                {picked.map((item) => (
                  <Combobox.Chip
                    key={getKey(item)}
                    data-slot='search-select-chip'
                    className={chipVariants()}
                  >
                    <span className='truncate'>{getLabel(item)}</span>
                    <Combobox.ChipRemove
                      className={chipRemoveVariants()}
                      aria-label={labels.remove(getLabel(item))}
                    >
                      <X size={12} aria-hidden='true' />
                    </Combobox.ChipRemove>
                  </Combobox.Chip>
                ))}
                <Combobox.Input
                  id={id}
                  aria-label={ariaLabel}
                  aria-describedby={ariaDescribedBy}
                  aria-invalid={invalid || undefined}
                  placeholder={picked.length === 0 ? placeholder : undefined}
                  className={cn(inputClass, 'min-w-32 px-2 py-1')}
                />
              </>
            )}
          </Combobox.Value>
        </Combobox.Chips>
      ) : (
        <Combobox.Input
          id={id}
          aria-label={ariaLabel}
          aria-describedby={ariaDescribedBy}
          aria-invalid={invalid || undefined}
          placeholder={placeholder}
          className={cn(inputClass, 'px-3.25 py-2.75')}
        />
      )}

      {clearable && hasValue && !disabled && (
        <Combobox.Clear className={controlClass} aria-label={labels.clear}>
          <X size={14} aria-hidden='true' />
        </Combobox.Clear>
      )}
      <Combobox.Trigger className={controlClass} aria-label={labels.open}>
        <ChevronDown className='size-2.5 stroke-3' aria-hidden='true' />
      </Combobox.Trigger>
    </Combobox.InputGroup>
  );
}
